/**
 * Finance domain error codes (IBA-163).
 *
 * One registry — an `enum`, per project convention, not a string-literal union — for the codes the finance
 * module attaches to error envelopes as `code`. The tenant app maps each to `finance.errors.<CODE>`.
 *
 * The enum VALUES equal the wire strings the services already emit, so adopting a member at a throw site
 * changes nothing on the wire — it only replaces a bare string literal with a checked reference. Budget codes
 * (BUDGET_*) are intentionally excluded: they belong to the budgeting domain (IBA-157).
 *
 * Adoption is incremental. Definition sites for setup, conversion-date and fiscal-period refusals reference
 * these members today; migrating the remaining inline FUND_* / JOURNAL_* / AMOUNT_* throw sites onto the
 * registry is mechanical, low-risk follow-through tracked under IBA-163.
 */
export enum FinanceErrorCode {
  SetupRequired = "FINANCE_SETUP_REQUIRED",
  // IBA-170. Distinct from SetupRequired on purpose: the identity IS set (which is why opening balances were
  // allowed to post), but the books have not been opened yet because the opening money is not fully allocated
  // across funds. The two send the user to different places, so they cannot share a code.
  NotActivated = "FINANCE_NOT_ACTIVATED",
  OpeningAllocationIncomplete = "OPENING_ALLOCATION_INCOMPLETE",
  OpeningAllocationLocked = "OPENING_ALLOCATION_LOCKED",
  // The other two ways Complete Finance Setup is refused. They were emitted as bare strings, which is how they
  // escaped this registry — and the tenant maps `finance.errors.<CODE>`, so an unregistered code has no copy.
  ParishFinanceSetupRequired = "PARISH_FINANCE_SETUP_REQUIRED",
  FinancialAccountRequired = "FINANCIAL_ACCOUNT_REQUIRED",
  ConversionDateViolation = "CONVERSION_DATE_VIOLATION",
  AccountWorkflowNotAllowed = "ACCOUNT_WORKFLOW_NOT_ALLOWED",
  FiscalPeriodClosed = "FISCAL_PERIOD_CLOSED",
  FiscalPeriodMissing = "FISCAL_PERIOD_MISSING",
  LedgerInvariantViolated = "LEDGER_INVARIANT_VIOLATED",
  JournalUnbalanced = "JOURNAL_UNBALANCED",
  JournalOriginalEntryNotFound = "JOURNAL_ORIGINAL_ENTRY_NOT_FOUND",
  AmountPrecisionExceedsCurrency = "AMOUNT_PRECISION_EXCEEDS_CURRENCY",
  AmountScaleInvalid = "AMOUNT_SCALE_INVALID",
  FundNotFound = "FUND_NOT_FOUND",
  FundNotActive = "FUND_NOT_ACTIVE",
  FundInvalid = "FUND_INVALID",
  FundRestricted = "FUND_RESTRICTED",
  FundScopeMismatch = "FUND_SCOPE_MISMATCH",
  FundCategoryNotAllowed = "FUND_CATEGORY_NOT_ALLOWED",
  FundOutsideAllowedPeriod = "FUND_OUTSIDE_ALLOWED_PERIOD",
  FundRestrictionNotConfigured = "FUND_RESTRICTION_NOT_CONFIGURED",
  // IBA-154 (D9). The expense spend-gate's code was never registered here, so the clearest rejection in the
  // whole fund flow — "that fund does not hold enough" — fell through to a generic message.
  InsufficientFundBalance = "INSUFFICIENT_FUND_BALANCE",
  FundRequired = "FUND_REQUIRED",
  IncomeFundNotSelectable = "INCOME_FUND_NOT_SELECTABLE",
  FundAllocationSplitRemoved = "FUND_ALLOCATION_SPLIT_REMOVED",
  FundReallocationSameFund = "FUND_REALLOCATION_SAME_FUND",
  // Guards added after independent review: a donor restriction cannot be reallocated away, a linked fund only
  // receives from its category, income whose designation has moved on cannot be reversed, and a category whose
  // fund still holds money cannot be archived.
  FundRestrictedCannotReallocate = "FUND_RESTRICTED_CANNOT_REALLOCATE",
  DestinationFundIsLinked = "DESTINATION_FUND_IS_LINKED",
  FundDesignationAlreadyMoved = "FUND_DESIGNATION_ALREADY_MOVED",
  LinkedFundStillHoldsMoney = "LINKED_FUND_STILL_HOLDS_MONEY",
  BudgetImpactChanged = "BUDGET_IMPACT_CHANGED",
}

/** Fast membership test for classifying a code string as a known finance domain code. */
export const FINANCE_ERROR_CODE_SET: ReadonlySet<string> = new Set(Object.values(FinanceErrorCode));
